import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  ANNOUNCEMENT_CATEGORIES,
  DalState,
  NewsCategoryInterface,
  NewsCategoryQueries,
  NewsInterface,
  NewsQueries,
  PersonInterface,
  PersonPreferenceActions,
  PersonPreferenceInterface,
  PersonPreferenceQueries,
  UserQueries,
} from '@campus/dal';
import { BehaviorSubject, combineLatest, filter, map, Observable, shareReplay, startWith, take } from 'rxjs';

import { WINDOW } from '@campus/browser';
import { select, Store } from '@ngrx/store';

@Injectable({ providedIn: 'root' })
export class NewsModalViewModel {
  private currentUser$: Observable<PersonInterface>;
  private currentUrl$: Observable<string>;
  private newsPopupCategoryIds$: Observable<number[]>;
  private newsWithCategories$: Observable<NewsInterface[]>;
  private newsAnnouncements$: Observable<NewsInterface[]>;
  private refresh$ = new BehaviorSubject<void>(undefined);
  private newsCategories$: Observable<NewsCategoryInterface[]>;
  private store: Store<DalState> = inject(Store);
  private router = inject(Router);
  private personPreferences$: Observable<PersonPreferenceInterface>;
  private window = inject(WINDOW);
  public newsAnnouncementsForUser$: Observable<{ user: PersonInterface; items: NewsInterface[] }>;

  constructor() {
    this.setSourceStream();
  }

  setSourceStream() {
    this.currentUser$ = this.store.pipe(select(UserQueries.getCurrentUser));

    this.currentUrl$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.personPreferences$ = this.store.pipe(
      select(PersonPreferenceQueries.getAll),
      map((pPs) => pPs.find((pP) => pP.key === 'NEWS'))
    );

    this.newsCategories$ = this.store.pipe(select(NewsCategoryQueries.getAll));
    this.newsPopupCategoryIds$ = this.newsCategories$.pipe(
      map((categories) => {
        return categories
          .filter((category) => ANNOUNCEMENT_CATEGORIES.includes(category.label))
          .map((category) => category.id);
      })
    );

    this.newsWithCategories$ = this.store.pipe(select(NewsQueries.getNewsWithMethodsAndCategories));

    this.newsAnnouncements$ = combineLatest([this.newsWithCategories$, this.newsPopupCategoryIds$]).pipe(
      map(([news, categoryIds]) =>
        news.filter((item) => (item.categories ?? []).some((category) => categoryIds.includes(category?.id)))
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.newsAnnouncementsForUser$ = combineLatest([
      this.newsAnnouncements$,
      this.currentUser$,
      this.currentUrl$,
      this.personPreferences$,
      this.refresh$,
    ]).pipe(
      filter(([, user]) => !!user),
      map(([items, user, url, personPreference]) => {
        const userRoles = user.roles ?? [];
        const hasUseRole = (newsItem) =>
          (newsItem.roleIds || []).some((roleId) => userRoles.some((userRole) => userRole.id === roleId));

        const visibleItems = items.filter(
          (item) =>
            this.isNewsItemVisibleOnUrl(url, item.allowedUrls) &&
            hasUseRole(item)
        );

        const readIds = (personPreference?.value as number[]) ?? [];
        const unreadItems = visibleItems.filter((item) => !readIds.includes(item.id));

        return {
          user,
          items: unreadItems,
        };
      })
    );
  }

  private isNewsItemVisibleOnUrl(currentUrl: string, allowedUrls: string | null): boolean {
    if (!allowedUrls) {
      return false;
    }

    const normalizedUrl = currentUrl.split('?')[0].split('#')[0];
    const browserUrl = this.window.location.href.split('?')[0].split('#')[0];

    return allowedUrls
      .split(/\r?\n|,/) // split by newline or comma
      .map((url) => url.trim())
      .some((pattern) => {
        const escapedPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp('^' + escapedPattern.replace(/\*/g, '.*') + '$'); // make * ready for regex
        return regex.test(normalizedUrl) || regex.test(browserUrl);
      });
  }

  private markNewsItemsAsReadForUser(user: PersonInterface, newsIds: number[]): void {
    this.personPreferences$.pipe(take(1)).subscribe((pP) => {
      const ids = pP?.value ?? [];
      const allIds = Array.from(new Set([...ids, ...newsIds]));
      const personPreference: PersonPreferenceInterface = {
        key: `NEWS`,
        value: allIds,
        name: 'Announcements that are read',
        personId: user.id,
      };
      this.store.dispatch(
        new PersonPreferenceActions.StartUpsertPersonPreference({ personPreference, noFeedBack: true })
      );
    });
  }

  public markNewsAsRead(items: NewsInterface[]): void {
    this.currentUser$.pipe(take(1)).subscribe((user) => {
      if (!user) {
        return;
      }

      this.markNewsItemsAsReadForUser(
        user,
        items.map((item) => item.id)
      );

      this.refresh$.next();
    });
  }
}
